/**
 * Token Metadata Service
 *
 * Provides symbol, name, decimals, and icon for any Solana token.
 * Uses a tiered lookup strategy:
 * 1. Local registry - Instant lookup for known tokens
 * 2. Memory cache - Avoid repeated API calls
 * 3. Helius DAS API - Fetch metadata for unknown tokens
 */
import { RPC_ENDPOINT } from "@solomon-apps/config";
import { WELL_KNOWN_TOKENS_STRINGS } from "@solomon-labs/constants";

// Token icons
import LegacyUSDvIcon from "../assets/icons/LegacyUSDv.png";
import sUSDvIcon from "../assets/icons/sUSDv.svg";
import MSOLIcon from "../assets/icons/tokens/msol.svg";
import PYUSDIcon from "../assets/icons/tokens/pyusd.svg";
import SOLIcon from "../assets/icons/tokens/sol.svg";
import USD1Icon from "../assets/icons/tokens/USD1.svg";
import USDGIcon from "../assets/icons/tokens/usdg.svg";
import USDCIcon from "../assets/icons/USDC.svg";
import USDTIcon from "../assets/icons/USDT.svg";
import USDvIcon from "../assets/logos/usdv-logomark.png";
import { LEGACY_USDV_MINT, USDV_DECIMALS, USDV_MINT } from "../tokens/usdv";

// Additional token addresses not in WELL_KNOWN_TOKENS_STRINGS
const USD1_ADDRESS = "USD1m8esQPzYdSYDZT9qvZf3msjrJqDqKiKSmRJQPro";
const USDG_ADDRESS = "2u1tszSeqZ3qBWF3uNGPFc8TzMk2tdiwknnRMWGWjGWH";
const MSOL_ADDRESS = "mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So";

/**
 * Token metadata structure
 */
export interface TokenMetadata {
    symbol: string;
    name?: string;
    decimals: number;
    icon?: string;
}

/**
 * Local registry mapping mint address to metadata
 */
const LOCAL_TOKEN_REGISTRY: Record<string, TokenMetadata> = {
    [WELL_KNOWN_TOKENS_STRINGS.SOL]: { symbol: "SOL", name: "Solana", decimals: 9, icon: SOLIcon },
    [WELL_KNOWN_TOKENS_STRINGS.USDC]: { symbol: "USDC", name: "USD Coin", decimals: 6, icon: USDCIcon },
    [WELL_KNOWN_TOKENS_STRINGS.USDT]: { symbol: "USDT", name: "Tether", decimals: 6, icon: USDTIcon },
    [USDV_MINT]: { symbol: "USDv", name: "USDv", decimals: USDV_DECIMALS, icon: USDvIcon },
    [LEGACY_USDV_MINT]: { symbol: "USDv", name: "Legacy USDv", decimals: USDV_DECIMALS, icon: LegacyUSDvIcon },
    [WELL_KNOWN_TOKENS_STRINGS.sUSDv]: { symbol: "sUSDv", name: "Staked USDv", decimals: 9, icon: sUSDvIcon },
    [WELL_KNOWN_TOKENS_STRINGS.PYUSD]: { symbol: "PYUSD", name: "PayPal USD", decimals: 6, icon: PYUSDIcon },
    [WELL_KNOWN_TOKENS_STRINGS.USDS]: { symbol: "USDS", name: "USDS", decimals: 6 },
    [WELL_KNOWN_TOKENS_STRINGS.USDe]: { symbol: "USDe", name: "Ethena USDe", decimals: 9 },
    [WELL_KNOWN_TOKENS_STRINGS.FDUSD]: { symbol: "FDUSD", name: "First Digital USD", decimals: 6 },
    [WELL_KNOWN_TOKENS_STRINGS.USDY]: { symbol: "USDY", name: "Ondo US Dollar Yield", decimals: 6 },
    [WELL_KNOWN_TOKENS_STRINGS.DUSD]: { symbol: "DUSD", name: "StandX DUSD", decimals: 6 },
    [WELL_KNOWN_TOKENS_STRINGS.sUSD]: { symbol: "sUSD", name: "Solayer USD", decimals: 9 },
    [USD1_ADDRESS]: { symbol: "USD1", name: "USD1", decimals: 6, icon: USD1Icon },
    [USDG_ADDRESS]: { symbol: "USDG", name: "USDG", decimals: 6, icon: USDGIcon },
    [MSOL_ADDRESS]: { symbol: "mSOL", name: "Marinade SOL", decimals: 9, icon: MSOLIcon }
};

/** In-memory cache for fetched metadata */
const tokenCache = new Map<string, TokenMetadata>();

/**
 * Helius DAS API response structure (partial)
 */
interface HeliusAssetResponse {
    result?: {
        content?: {
            metadata?: {
                symbol?: string;
                name?: string;
            };
            links?: {
                image?: string;
            };
            files?: Array<{ uri?: string }>;
        };
        token_info?: {
            decimals?: number;
        };
    };
}

/**
 * Fetch token metadata from Helius DAS API
 */
async function fetchHeliusMetadata(mint: string): Promise<TokenMetadata | null> {
    try {
        const response = await fetch(RPC_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                jsonrpc: "2.0",
                id: 1,
                method: "getAsset",
                params: { id: mint }
            })
        });

        if (!response.ok) return null;

        const data: HeliusAssetResponse = await response.json();
        const result = data.result;

        if (!result?.content?.metadata) return null;

        return {
            symbol: result.content.metadata.symbol ?? "???",
            name: result.content.metadata.name,
            decimals: result.token_info?.decimals ?? 9,
            icon: result.content.links?.image ?? result.content.files?.[0]?.uri
        };
    } catch (error) {
        console.error("[TokenMetadata] Helius fetch failed:", error);
        return null;
    }
}

/**
 * Get token metadata with tiered lookup:
 * 1. Local registry (instant)
 * 2. Memory cache (instant)
 * 3. Helius DAS API (async)
 */
export async function getTokenMetadata(mint: string): Promise<TokenMetadata> {
    // 1. Check local registry
    const local = LOCAL_TOKEN_REGISTRY[mint];
    if (local) return local;

    // 2. Check memory cache
    const cached = tokenCache.get(mint);
    if (cached) return cached;

    // 3. Fetch from Helius
    const fetched = await fetchHeliusMetadata(mint);

    if (fetched) {
        tokenCache.set(mint, fetched);
        return fetched;
    }

    // 4. Fallback for unknown tokens
    const fallback: TokenMetadata = {
        symbol: `${mint.slice(0, 4)}...${mint.slice(-4)}`,
        decimals: 9
    };
    tokenCache.set(mint, fallback);
    return fallback;
}

/**
 * Synchronous lookup - only checks local registry and cache
 * Returns null if not found (caller should fetch async)
 */
export function getTokenMetadataSync(mint: string): TokenMetadata | null {
    return LOCAL_TOKEN_REGISTRY[mint] ?? tokenCache.get(mint) ?? null;
}
